import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiAuthError, fetchUsage, resetUserQuota } from "../api/client";
import type { UsageUserQuota, UsageWindow } from "../api/types";
import { SignInScreen } from "../auth/SignInScreen";
import { useToken } from "../auth/TokenContext";
import { KpiCard } from "../components/KpiCard";

const QUOTA_COLORS = {
  ok: "#22c55e",
  warn: "#f59e0b",
  critical: "#ef4444",
} as const;

const WINDOWS: Array<{ value: UsageWindow; label: string }> = [
  { value: "7d", label: "7 days (quota)" },
  { value: "14d", label: "14 days" },
  { value: "30d", label: "30 days" },
];

function quotaStatus(
  pct: number,
  allowed: boolean,
): "ok" | "warn" | "critical" {
  if (!allowed) return "critical";
  if (pct >= 90) return "critical";
  if (pct >= 60) return "warn";
  return "ok";
}

function formatCost(usd: number): string {
  if (usd >= 1000) return `$${(usd / 1000).toFixed(1)}k`;
  if (usd >= 100) return `$${Math.round(usd)}`;
  return `$${usd.toFixed(2)}`;
}

type QuotaSortField = "userId" | "spentDollars" | "pctUsed" | "budgetDollars";
type HistoricalSortField = "userId" | "spentDollars";
type SortField = QuotaSortField | HistoricalSortField;
type SortDir = "asc" | "desc";

export function Usage(): JSX.Element {
  const { token, signOut } = useToken();
  const queryClient = useQueryClient();
  const tokenKey = token?.slice(-8);

  const [selectedWindow, setSelectedWindow] = useState<UsageWindow>("7d");
  const [sortBy, setSortBy] = useState<SortField>("spentDollars");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [resettingUser, setResettingUser] = useState<string | null>(null);

  const isQuotaView = selectedWindow === "7d";

  const usageQuery = useQuery({
    queryKey: ["usage", tokenKey, selectedWindow],
    queryFn: () => fetchUsage(selectedWindow, token),
    enabled: !!token,
    refetchInterval: selectedWindow === "7d" ? 30_000 : 120_000,
  });

  const authError =
    usageQuery.error instanceof ApiAuthError ? usageQuery.error : null;
  if (authError) {
    return (
      <SignInScreen
        reason={`Token rejected (HTTP ${authError.status}): ${authError.message}`}
      />
    );
  }

  const users = usageQuery.data?.users ?? [];
  const isAdmin = usageQuery.data?.isAdmin ?? false;

  // Narrow to quota shape for the 7d view.
  const quotaUsers = isQuotaView ? (users as UsageUserQuota[]) : [];

  const sorted = [...users].sort((a, b) => {
    let av: string | number;
    let bv: string | number;
    if (sortBy === "userId") {
      av = a.userId;
      bv = b.userId;
    } else if (sortBy === "spentDollars") {
      av = a.spentDollars;
      bv = b.spentDollars;
    } else {
      // quota-only fields (pctUsed, budgetDollars) — only reachable when isQuotaView
      const qa = a as UsageUserQuota;
      const qb = b as UsageUserQuota;
      av =
        qa[sortBy as keyof Pick<UsageUserQuota, "pctUsed" | "budgetDollars">];
      bv =
        qb[sortBy as keyof Pick<UsageUserQuota, "pctUsed" | "budgetDollars">];
    }
    if (typeof av === "string" && typeof bv === "string") {
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    const an = av as number;
    const bn = bv as number;
    return sortDir === "asc" ? an - bn : bn - an;
  });

  // KPIs
  const totalSpend = users.reduce((s, u) => s + u.spentDollars, 0);
  const avgSpend = users.length > 0 ? totalSpend / users.length : 0;
  const usersOver80 = quotaUsers.filter((u) => u.pctUsed >= 80).length;
  const usersBlocked = quotaUsers.filter((u) => !u.allowed).length;

  const handleWindowChange = (w: UsageWindow): void => {
    setSelectedWindow(w);
    if (w !== "7d") {
      // Always reset to default sort for historical views: highest spend first.
      // Quota-only fields (pctUsed, budgetDollars) don't exist on historical
      // rows; ascending direction from the quota view would show lowest spend.
      setSortBy("spentDollars");
      setSortDir("desc");
    }
  };

  const handleSort = (field: SortField): void => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  };

  const handleReset = async (userId: string): Promise<void> => {
    if (
      !window.confirm(`Reset spend quota for ${userId}? This cannot be undone.`)
    )
      return;
    setResettingUser(userId);
    try {
      await resetUserQuota(userId, token);
      await queryClient.invalidateQueries({ queryKey: ["usage"] });
      await queryClient.invalidateQueries({ queryKey: ["quotas"] });
    } catch (err) {
      alert(
        `Reset failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setResettingUser(null);
    }
  };

  // colSpan for loading/empty rows:
  // 7d: User + Origin + Spent + Budget + % Used + Resets + (Actions) = 6 or 7
  // 14d/30d: User + Origin + Spent = 3
  const colCount = isQuotaView ? (isAdmin ? 7 : 6) : 3;

  const windowLabel = WINDOWS.find((w) => w.value === selectedWindow)?.label;

  return (
    <section className="overview">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>
          {isQuotaView
            ? "Spend Quota — rolling 7 days"
            : `User Spend — last ${windowLabel}`}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Window selector */}
          <div
            className="time-range-presets"
            role="tablist"
            aria-label="Time window"
          >
            {WINDOWS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={selectedWindow === value}
                className={
                  selectedWindow === value
                    ? "time-range-chip time-range-chip-active"
                    : "time-range-chip"
                }
                onClick={() => handleWindowChange(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="refresh-btn"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["usage"] })
            }
            disabled={usageQuery.isFetching}
          >
            {usageQuery.isFetching ? "⟳ Loading…" : "⟳ Refresh"}
          </button>
        </div>
      </div>

      {usageQuery.isError && !authError && (
        <div className="overview-error">
          Failed to load usage data.{" "}
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      )}

      <div className="kpi-strip">
        <KpiCard
          label={`Total spend (${selectedWindow})`}
          value={formatCost(totalSpend)}
          delta={null}
          loading={usageQuery.isLoading}
          hint={`Sum of all users' rolling ${selectedWindow} spend`}
        />
        {isQuotaView && (
          <KpiCard
            label="Users > 80% quota"
            value={String(usersOver80)}
            delta={null}
            loading={usageQuery.isLoading}
            hint="Users who have used more than 80% of their budget"
          />
        )}
        {isQuotaView && (
          <KpiCard
            label="Users blocked"
            value={String(usersBlocked)}
            delta={null}
            loading={usageQuery.isLoading}
            hint="Users who have exceeded their budget (HTTP 429)"
          />
        )}
        <KpiCard
          label="Avg spend / user"
          value={formatCost(avgSpend)}
          delta={null}
          loading={usageQuery.isLoading}
          hint={`Average ${selectedWindow} spend per active user`}
        />
      </div>

      <section className="chart-section" style={{ marginTop: 24 }}>
        <div className="sessions-table-wrapper">
          <table className="sessions-table">
            <thead>
              <tr>
                <SortHeader
                  label="User"
                  field="userId"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={handleSort}
                />
                <th>Origin</th>
                <SortHeader
                  label={`Spent (${selectedWindow})`}
                  field="spentDollars"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onClick={handleSort}
                />
                {isQuotaView && (
                  <SortHeader
                    label="Budget"
                    field="budgetDollars"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={handleSort}
                  />
                )}
                {isQuotaView && (
                  <SortHeader
                    label="% Used"
                    field="pctUsed"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={handleSort}
                  />
                )}
                {isQuotaView && <th>Resets</th>}
                {isQuotaView && isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {usageQuery.isLoading && (
                <tr>
                  <td colSpan={colCount} className="sessions-table-loading">
                    Loading usage data…
                  </td>
                </tr>
              )}
              {!usageQuery.isLoading && sorted.length === 0 && (
                <tr>
                  <td colSpan={colCount} className="sessions-table-empty">
                    No usage data in the last {windowLabel}.
                  </td>
                </tr>
              )}
              {sorted.map((u) => {
                if (isQuotaView) {
                  const qu = u as UsageUserQuota;
                  const status = quotaStatus(qu.pctUsed, qu.allowed);
                  return (
                    <tr key={qu.userId} className="sessions-table-row">
                      <td className="sessions-table-user" title={qu.userId}>
                        {qu.userId}
                      </td>
                      <td>
                        <OriginBadge origin={qu.origin} />
                      </td>
                      <td className="sessions-table-events">
                        {formatCost(qu.spentDollars)}
                      </td>
                      <td className="sessions-table-events">
                        {formatCost(qu.budgetDollars)}
                      </td>
                      <td className="sessions-table-events">
                        <QuotaBar pct={qu.pctUsed} status={status} />
                      </td>
                      <td className="sessions-table-time">
                        {new Date(qu.resetsAt).toLocaleDateString("en-US", {
                          timeZone: "UTC",
                        })}
                      </td>
                      {isAdmin && (
                        <td>
                          <button
                            type="button"
                            onClick={() => handleReset(qu.userId)}
                            disabled={resettingUser === qu.userId}
                            style={{
                              padding: "2px 8px",
                              fontSize: 11,
                              border: "1px solid #ef4444",
                              borderRadius: 4,
                              color: "#ef4444",
                              backgroundColor: "transparent",
                              cursor:
                                resettingUser === qu.userId
                                  ? "wait"
                                  : "pointer",
                              opacity: resettingUser === qu.userId ? 0.5 : 1,
                            }}
                          >
                            {resettingUser === qu.userId ? "…" : "Reset"}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                }
                // Historical view — simple row.
                return (
                  <tr key={u.userId} className="sessions-table-row">
                    <td className="sessions-table-user" title={u.userId}>
                      {u.userId}
                    </td>
                    <td>
                      <OriginBadge origin={u.origin} />
                    </td>
                    <td className="sessions-table-events">
                      {formatCost(u.spentDollars)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function SortHeader({
  label,
  field,
  sortBy,
  sortDir,
  onClick,
}: {
  label: string;
  field: SortField;
  sortBy: SortField;
  sortDir: SortDir;
  onClick: (f: SortField) => void;
}): JSX.Element {
  const arrow = sortBy === field ? (sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <th
      style={{ cursor: "pointer", userSelect: "none" }}
      onClick={() => onClick(field)}
    >
      {label}
      {arrow}
    </th>
  );
}

function OriginBadge({ origin }: { origin: string }): JSX.Element {
  const color = origin === "adobe" ? "#6366f1" : "#8b5cf6";
  return (
    <span
      className="badge badge-outcome"
      style={{
        backgroundColor: `${color}22`,
        color,
      }}
    >
      {origin === "adobe" ? "Adobe" : "Non-Adobe"}
    </span>
  );
}

function QuotaBar({
  pct,
  status,
}: {
  pct: number;
  status: "ok" | "warn" | "critical";
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div
        style={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          backgroundColor: "#e5e7eb",
          overflow: "hidden",
          minWidth: 60,
        }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 3,
            width: `${Math.min(100, pct)}%`,
            backgroundColor: QUOTA_COLORS[status],
            transition: "width 0.3s",
          }}
        />
      </div>
      <span style={{ fontSize: 12, minWidth: 36, textAlign: "right" }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}
